import {useEffect, useRef, useState} from 'react';
import { ATTENDANCE_SUBVIEW_MODEL, TABS_ATTENDANCE } from '../../../Model/Model';
import {useLocation, useNavigate} from 'react-router-dom';
import routes from '../../../../../../../Routes/routes';
import { useTranslation } from 'react-i18next';
import { PHOTO_PICKER_MODEL } from '../../../../../../../components/PhotoPicker/SubComponent/Models/Models';
import {
    AdjustAttendanceSubmissionGroup,
    GetAttendanceAdjustmentGroupPendingList,
    GetAttendancePeriodReview
} from "../../../../../../../reduxToolkit/AttendanceAdjustment/AttendanceAdjustmentApi";
import {calculateHeight, errorMsg, isSmWidth} from "../../../../../../../Apis/functions";
import {useDispatch, useSelector} from "react-redux";
import {toast} from "react-toastify";
import {SUBMISSION_PERIOD_REVIEW_ITEM_MODEL} from "../Model/Model";
import HandleChange from "../../../../../../../Helpers/HandleChange";
/**
 *
 * @Author: Susana García Rodríguez
 * @JobTitle : Frontend Developer - IBL
 * @Email : sgarcia@ibl.mx
 *
 */
const UpdatePeriodReviewApp = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { isAttendanceSupervisor } = useSelector((state) => state.AttendancePersistSlice);
    const {getAttendancePeriodReview} = useSelector((state) => state.AttendanceAdjustmentSlice);
    const { state } = location; // Get state from location
    const containerCardRef = useRef(null);
    const containerTableRef = useRef(null);
    const contentCardRef = useRef(null);
    const contentTableRef = useRef(null);
    const [modified, setModified] = useState(false);
    const [periodReviewItem, setPeriodReviewItem] = useState({...SUBMISSION_PERIOD_REVIEW_ITEM_MODEL});
    const [objectItems, setObjectItems] = useState(null);
    const [photoPicker, setPhotoPicker] = useState({ ...PHOTO_PICKER_MODEL });
    const [emptyFields, setEmptyFields] = useState(false);
    const { handleChange } = HandleChange({ model: periodReviewItem, setModel: setPeriodReviewItem });

    /**
     * Initializes dynamic height calculations for layout containers.
     * Skips execution on small screens.
     */
    useEffect(() => {
        if (isSmWidth()) return;
        const bottom = 80;
        calculateHeight({elementRef: containerCardRef, bottom:bottom});
        calculateHeight({elementRef: containerTableRef, bottom:bottom});
        calculateHeight({elementRef: contentCardRef, bottom:bottom});
        calculateHeight({elementRef: contentTableRef, bottom:bottom});
    }, []);

    /**
     * Loads attendance period review and then fetches pending data.
     * Runs once on component mount.
     */
    useEffect(() => {
        dispatch(GetAttendancePeriodReview()).then(
            ({
                 payload: {
                     data: { success, code },
                 }
             }) => {
            if (success){
                getInfo();
            }else errorMsg(code);
        });
    }, []); // Dependency array to trigger re-fetching on update

    /**
     * Re-fetches data when modifications occur.
     */
    useEffect(() => {
        if(modified){
            getInfo();
            setModified(false);
        }
    }, [modified]); // Dependency array to trigger re-fetching on update

    /**
     * Fetches pending attendance adjustment groups and flattens them
     * into a single list of items for UI consumption.
     *
     * @function getInfo
     * @returns {void}
     */
    const getInfo = () => {
        dispatch(GetAttendanceAdjustmentGroupPendingList()).then(
            ({
                 payload: {
                     data: { data, success, code },
                 }
             }) => {
                if (success) {
                    let aux = [];
                    data.forEach(e => {
                        aux = aux.concat(e.objectItems);
                    });

                    setObjectItems(aux);
                }else errorMsg(code);
            }
        );
    };

    /**
     * Navigates back to the main attendance view,
     * preserving current tab and subview state.
     *
     * @function handleOnBack
     * @returns {void}
     */
    const handleOnBack = () => {
        navigate(routes.attendance.main, {
            state: {
                currentTab: TABS_ATTENDANCE.SUBMISSIONS.INDEX,
                currentSubView: ATTENDANCE_SUBVIEW_MODEL.SUBMISSIONS.PERIOD_REVIEW,
                // The list is filtered by department, so it travels back with the tab
                // to rebuild the view the user came from.
                periodReviewFilters: state?.periodReviewFilters
            },
        });
    };

    /**
     * Validates form data and submits attendance updates.
     *
     * Flow:
     * - Validates required fields using periodReviewItem
     * - Sends update request with attached files
     * - Shows success or error feedback
     * - Redirects on success
     *
     * @function handleUpdate
     * @returns {void}
     */
    const handleUpdate = () => {
        if (!periodReviewItem.isValid()) {
            setEmptyFields(true);
            return;
        }

        setEmptyFields(false);
        const files = photoPicker.listFiles;
        dispatch(AdjustAttendanceSubmissionGroup(periodReviewItem.toRegister({ files }))).then(
            ({
                 payload: {
                     data: { success, code },
                 }
             }) => {
                if (success) {
                    toast.success(t("has_been_updated_successfully"));
                    handleOnBack();
                }else errorMsg(code);
            }
        );
    };

    return {
        DEV,
        permission,
        isAttendanceSupervisor,
        navigate,
        handleOnBack,
        photoPicker,
        setPhotoPicker,
        handleUpdate,
        emptyFields,
        containerCardRef,
        containerTableRef,
        contentCardRef,
        contentTableRef,
        objectItems,
        handleChange,
        periodReviewItem,
        getAttendancePeriodReview
    };
};

export default UpdatePeriodReviewApp;
